/**
 * Dice Controller - Manages dice throwing mechanic
 *
 * Implements the v2 pickup-throw system:
 * - Energy accumulation from swipe movement
 * - Multi-dice pickup
 * - Directional throw with accumulated power
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import gsap from 'gsap';
import { StateMachine } from '@sdl/shared';
import { DiceThrowState, DiceThrowContext, DicePhase, DiceThrowParams } from '@sdl/shared';
import { Engine } from '../../engine/Engine';
import { EventEmitter, DiceEvents } from '../../engine/core/EventEmitter';
import { sharedRaycaster, sharedPointer, tempPlane, tempVec3A, tempVec3B } from '../../engine/core/SharedResources';
import { Dice } from './Dice';
import { preRunThrow } from './ThrowPreRun';
import { sound, SoundId } from '../../engine/audio/sound';
import { randomShakeSound } from '../../engine/audio/SoundCatalog';
import { renderScheduler } from '../../engine/render/RenderScheduler';
import { loadDefaultShootingConfig } from './ShootingConfig';
import type { DiceAssetManager } from './assets/DiceAssetManager';
import { isMobilePlatform } from '../../util/platform';

// Module-scope scratch (hot paths run per pointer-move / per frame — no
// per-call allocation). _groundScratch is consumed synchronously by
// findDiceNearPoint; _heldTarget/_heldLerped live only within one loop body.
const _groundScratch = new THREE.Vector3();
const _heldTarget = new THREE.Vector3();
const _heldLerped = new THREE.Vector3();

export interface DiceControllerConfig {
  diceCount?: number;
  energyGain?: number;      // Energy gain per pixel of movement
  maxEnergy?: number;
  minEnergyToThrow?: number;
  maxPower?: number;
  baseForward?: number;     // Forward force multiplier
  holdHeight?: number;      // Height when holding dice
  pickupRadius?: number;    // Radius to pick up dice
  lobStrength?: number;     // Upward lift force (0 = none, 1 = strong gust)
  lobDuration?: number;     // How long the lift lasts in seconds (0.1 - 0.5)
  diceMass?: number;        // Dice weight (0.5 = light, 1 = normal, 3+ = heavy)
  /**
   * Multiplier applied to `energyGain` on Android / iOS only. A screen
   * pixel on a phone is physically ~2-3× smaller than on a desktop
   * monitor, so the same finger travel registers less throw energy
   * than the equivalent mouse swipe. Touch-laptops are excluded —
   * they use a mouse-like grip and don't need the boost.
   */
  touchEnergyMultiplier?: number;
}

const DEFAULT_CONFIG: Required<DiceControllerConfig> = {
  diceCount: 3,
  energyGain: 0.3,
  maxEnergy: 100,
  minEnergyToThrow: 18,
  maxPower: 60,
  baseForward: 5,
  holdHeight: 4,
  pickupRadius: 1.5,
  lobStrength: 1.0,
  lobDuration: 0.2,
  diceMass: 1.0,
  touchEnergyMultiplier: 1.5,
};

// Minimum pointer movement (pixels) before accumulating throw energy (holding state uses HOLD_DEADZONE_PX)
// Note: Desktop mouse events have smaller deltas (1-3px) than mobile touch, so keep threshold low
const HOLD_DEADZONE_PX = 2;
// Gesture spin constants (currently unused; keep for future iteration)
// const CURVE_ARC_THRESHOLD = 0.6;     // radians of accumulated arc needed to trigger spin
// const CURVE_PATH_THRESHOLD = 24;     // pixels of path length to consider curve meaningful
// const SPIN_SCALE = 24;               // scales arc magnitude into spin
// const SPIN_MAX = 28;                 // clamp spin to avoid excessive angular velocity
// const SPIN_MIN = 4;                  // minimum spin when a curve is detected
// Pixels of pointer path (within the current press) before a release counts as
// a throw. Below this the dice DROP back to the table and must be picked up
// again — a grab-and-twitch release is not a throw. Tuned together with
// minEnergyToThrow (18 energy ≈ 60px of motion at 0.3/px): one deliberate
// flick clears both gates, a few-pixel nudge clears neither.
const MIN_THROW_PATH = 70;
// ── Throw pre-run (stuck-dice prevention) — see ThrowPreRun.ts ──────────────
// A throw must fully settle within this many simulated frames (4.5s @ 60fps,
// Phone Dice uses 300) or it's re-randomized. Generous: clean rolls sleep in
// ~120-180 frames; only wall-wedges and perpetual sliders blow the budget.
const PRERUN_FRAME_BUDGET = 270;
// Evaluations per throw before giving up (each costs one headless sim).
const PRERUN_MAX_ATTEMPTS = 5;
// Wall-clock guard: stop regenerating past this so a slow device's throw
// release never hitches noticeably.
const PRERUN_TIME_BUDGET_MS = 120;
// Shake rattle (HOLDING): a completed horizontal stroke must travel this far
// for its reversal to count as a shake beat — filters jitter and slow drift.
// Mobile needs both knobs higher: wrist shakes reverse direction much faster
// than mouse shakes and finger micro-jitter fakes reversals, so phone beats
// pinned to the desktop cooldown read as a machine gun (POCO F3 repro).
const SHAKE_STROKE_MIN_PX = 14;
const SHAKE_STROKE_MIN_PX_MOBILE = 26;
// Min gap between rattle plays. Pool 4 + pitch jitter on DICE_PICKUP make
// overlapping beats read as a natural dice rattle, not a machine gun.
const SHAKE_SOUND_COOLDOWN_MS = 140;
const SHAKE_SOUND_COOLDOWN_MS_MOBILE = 210;
// Shake-beat intensity: stroke speed (px/ms over the completed stroke) maps
// onto rattle volume. A lazy waggle plays at SHAKE_VOLUME_MIN of the catalog
// volume, a vigorous flick at full. Same scale feeds the relay so opponents
// hear how hard you're shaking.
const SHAKE_SPEED_SOFT = 0.25;  // px/ms — at/below this → minimum volume
const SHAKE_SPEED_HARD = 1.4;   // px/ms — at/above this → full volume
const SHAKE_VOLUME_MIN = 0.45;  // volume multiplier floor at soft speed
const SHAKE_VOLUME_MAX = 0.6;   // hardest flick tops out here (rattle stays background)

export class DiceController extends EventEmitter<DiceEvents> {
  private engine: Engine;
  private config: Required<DiceControllerConfig>;
  private dice: Dice[] = [];
  private stateMachine: StateMachine<DiceThrowContext, DiceThrowState>;
  private enabled = true;

  // Tracking
  private lastInputPosition = { x: 0, y: 0 };
  private accumulatedEnergy = 0;
  // Shake-rattle detection while HOLDING: a horizontal direction reversal
  // after a long-enough stroke is one "shake beat" → pickup sample plays as
  // a hand rattle. Scalar state only — no allocations, no per-frame work.
  private shakeDir = 0;
  private shakeStrokePx = 0;
  private shakeStrokeStartMs = 0;
  private lastShakeSoundTime = 0;
  private collectedDice: Set<Dice> = new Set();
  private lastEnergyUpdateTime = 0;
  private gesturePoints: Array<{ x: number; y: number }> = [];
  private gesturePathLength = 0;
  private gestureArcAngle = 0;

  // Hold animation
  private holdTime = 0;

  // Finger tracking for dice following
  private holdPosition = new THREE.Vector3();
  private isHolding = false;
  private readonly MAGNET_SPEED = 25;

  // Track if finger is currently touching (even during rolling/settling)
  private isTouching = false;
  private touchPosition = { x: 0, y: 0 };

  // Network sync
  private dragSyncInterval: ReturnType<typeof setInterval> | null = null;
  private readonly DRAG_SYNC_INTERVAL = 100; // Send position every 100ms for smooth sync
  private dragSeq = 0;

  // Server-authoritative state
  private isAuthorized = false;           // Server authorized us to roll
  private serverDicePhase: DicePhase = DicePhase.IDLE;
  private isServerAuthoritativeMode = false; // When true, wait for server events

  // HI_LO determinism: defer physics until server echo
  // When true, onThrowingEnter captures state and sends to server but keeps dice frozen.
  // Physics only starts when applyServerThrow() arrives.
  private deferPhysicsOnThrow = false;
  // onStoppedEnter behavior: true = park settled dice as non-colliders
  // (single-shooter phases), false = leave them live sleeping colliders
  // (HI_LO knockable-settled-dice model). See setParkOnStop.
  private parkOnStop = true;

  // Pickup label sprites (float above dice when waiting to be picked up)
  private pickupLabels: THREE.Sprite[] = [];
  private static pickupTexture: THREE.Texture | null = null;
  private static pickupTextureLoading = false;
  private pickupLabelsVisible = false;
  /** Pop-in scale multiplier animated by GSAP (0→1) */
  private pickupLabelPopScale = 0;

  // Dice asset manager (custom GLB dice skins)
  private assetManager: DiceAssetManager | null = null;
  private equippedDice: (string | null)[] = [];

  // Lob effect (upward wind gust during throw)
  // (lobActive / lobElapsed / lobPower removed with the dead per-frame
  // lob-force block — see the note in update(); git history has the design.)

  constructor(engine: Engine, config: DiceControllerConfig = {}) {
    super();

    this.engine = engine;

    // Load shooting config from localStorage (editor presets) and merge with defaults
    // Priority: passed config > localStorage preset > hardcoded defaults
    const savedShootingConfig = loadDefaultShootingConfig();
    this.config = { ...DEFAULT_CONFIG, ...savedShootingConfig, ...config };

    // Initialize state machine
    this.stateMachine = new StateMachine<DiceThrowContext, DiceThrowState>({
      id: 'dice-throw',
      initial: DiceThrowState.READY,
      context: {
        diceCount: this.config.diceCount,
        diceCollected: 0,
        energy: 0,
        maxEnergy: this.config.maxEnergy,
        minEnergyToThrow: this.config.minEnergyToThrow,
        swipeDirection: { x: 0, y: 0 },
        swipeVelocity: 0,
        dicePositions: [],
        diceRotations: [],
        diceResults: [],
        rollStartTime: 0,
        settleStartTime: 0,
      },
      states: [
        {
          id: DiceThrowState.READY,
          onEnter: () => this.onReadyEnter(),
          transitions: [
            { to: DiceThrowState.PICKING_UP },
          ],
        },
        {
          id: DiceThrowState.PICKING_UP,
          onEnter: () => this.onPickingUpEnter(),
          onUpdate: (_ctx, dt) => this.onPickingUpUpdate(dt),
          transitions: [
            { to: DiceThrowState.HOLDING, guard: () => this.allDiceCollected() },
            { to: DiceThrowState.READY },
          ],
        },
        {
          id: DiceThrowState.HOLDING,
          onEnter: () => this.onHoldingEnter(),
          onUpdate: (_ctx, dt) => this.onHoldingUpdate(dt),
          transitions: [
            { to: DiceThrowState.THROWING },
            { to: DiceThrowState.READY },
          ],
        },
        {
          id: DiceThrowState.THROWING,
          onEnter: () => this.onThrowingEnter(),
          transitions: [
            { to: DiceThrowState.ROLLING },
          ],
        },
        {
          id: DiceThrowState.ROLLING,
          onEnter: () => this.onRollingEnter(),
          onUpdate: (_ctx, dt) => this.onRollingUpdate(dt),
          transitions: [
            { to: DiceThrowState.SETTLING, guard: () => this.areDiceSlowing() },
          ],
        },
        {
          id: DiceThrowState.SETTLING,
          onEnter: () => this.onSettlingEnter(),
          onUpdate: (_ctx, dt) => this.onSettlingUpdate(dt),
          transitions: [
            { to: DiceThrowState.STOPPED, guard: () => this.areDiceStopped() },
          ],
        },
        {
          id: DiceThrowState.STOPPED,
          onEnter: () => this.onStoppedEnter(),
          transitions: [
            { to: DiceThrowState.READY },
          ],
        },
      ],
      // Edge-driven throw-state notification. Consumers that mirror the
      // throw lifecycle (instruction-banner suppression, pickup-label
      // visibility) react here instead of polling `state` per frame.
      onStateChange: (_from, to) => {
        this.emit('dice:throw_state', { state: to });
        this.syncPickupLabelVisibility();
      },
    });

    // Setup input handlers
    this.setupInputHandlers();

    // Preload pickup label texture
    this.ensurePickupTexture();
  }

  private setupInputHandlers(): void {
    // Touch/pointer start
    this.engine.on('input:touch_start', (data) => this.onInputStart(data.x, data.y));
    this.engine.on('input:pointer_down', (data) => this.onInputStart(data.x, data.y));

    // Touch/pointer move
    this.engine.on('input:touch_move', (data) => this.onInputMove(data.x, data.y));
    this.engine.on('input:pointer_move', (data) => this.onInputMove(data.x, data.y));

    // Touch/pointer end
    this.engine.on('input:touch_end', (data) => this.onInputEnd(data.x, data.y));
    this.engine.on('input:pointer_up', (data) => this.onInputEnd(data.x, data.y));
  }

  /** Set the dice asset manager for loading custom dice skins */
  setAssetManager(manager: DiceAssetManager): void {
    this.assetManager = manager;
  }

  /** The shared dice asset manager (same instance the opponent dice use). */
  getAssetManager(): DiceAssetManager | null {
    return this.assetManager;
  }

  /** Set the equipped dice IDs (from active preset slots) */
  setEquippedDice(diceIds: (string | null)[]): void {
    this.equippedDice = diceIds;
  }

  /**
   * After a live equipped-dice swap, rebuild the local dice in place so the new
   * skin shows immediately — but ONLY when they're at rest on the felt, never
   * mid-pickup / hold / throw / roll. "At rest" = READY (idle before pickup) OR
   * STOPPED (landed from the previous throw, waiting for the next pickup — the
   * between-rolls window in craps continue-roll, where the dice persist on the
   * felt). createDice re-reads equippedDice and gates each slot on asset
   * readiness, so call this once the swap's preload has resolved. No-op if no
   * dice are spawned (betting: the next createDice already picks up the new skin).
   */
  refreshEquippedAtRest(): boolean {
    const atRest = this.state === DiceThrowState.READY || this.state === DiceThrowState.STOPPED;
    if (!atRest || this.dice.length === 0) return false;
    // Spawn slightly above each die's resting spot so the new skin drops in and
    // settles (physics fall + clatter) — tactile feedback that the swap landed.
    const DROP_HEIGHT = 1.4;
    const positions = this.dice.map((d) => ({
      x: d.mesh.position.x,
      y: d.mesh.position.y + DROP_HEIGHT,
      z: d.mesh.position.z,
    }));
    this.createDice(positions);
    // createDice reassigns `this.dice` — anything holding the old array (the
    // CameraController tracks dice by reference) is now stale. Caller must
    // re-sync via getDice(). Return true so it knows the array changed.
    return true;
  }

  /** Get the equipped dice IDs */
  getEquippedDice(): (string | null)[] {
    return this.equippedDice;
  }

  /**
   * Initialize dice
   */
  createDice(positions: Array<{ x: number; y: number; z: number }>): void {
    // Clear existing dice (release assets back to pool)
    this.releaseDiceAssets();
    this.dice = [];

    // Create new dice — the equipped skin, else the starter red die for an
    // empty/unloaded slot (real face map), else the procedural fallback.
    for (let i = 0; i < positions.length; i++) {
      const diceId = this.equippedDice[i] ?? null;
      const asset = this.assetManager?.acquireDiceOrDefault(diceId) ?? null;

      const die = new Dice(this.engine.physics, this.engine.scene, {
        position: positions[i],
        size: 1,
        color: 0xcc0000,
      }, asset ?? undefined);

      this.dice.push(die);
    }
    // Dice count changed — re-evaluate pickup-label visibility (edge-driven).
    this.syncPickupLabelVisibility();
  }

  /**
   * Freeze the local player's dice (mass 0 / mask NONE) so a REMOTE shooter's
   * replay can't collide with our leftover dice — e.g. the local banker's
   * settled dice still sitting on the table when a remote player rolls next.
   * Part of the single-shooter-world guarantee (GameFlowController.loadShooter,
   * mirroring Phone Dice loadPlayer). Transparent to our own next throw: the
   * pickup→release path restores full collision. No-op while we hold nothing.
   */
  parkLeftoverDice(): void {
    for (const die of this.dice) {
      die.parkAtRest();
    }
  }

  clearDice(): void {

    this.disposePickupLabels();
    this.releaseDiceAssets();
    this.dice = [];
    this.collectedDice.clear();
    this.accumulatedEnergy = 0;
    this.isHolding = false;
    this.stateMachine.forceTransitionTo(DiceThrowState.READY);
    // dice=[] means labels must be down even if the state edge above no-oped
    // (already READY). disposePickupLabels cleared the flag; this re-checks.
    this.syncPickupLabelVisibility();
  }

  /**
   * Reset the throw state machine to READY without touching the dice on the
   * table. WAITING entry needs this: a mid-game void can land there right
   * after a local throw (controller settled at STOPPED), and the per-frame
   * banner suppression reads `state !== READY` — without a reset the
   * "waiting for players" banner stays suppressed forever. clearDice() also
   * resets, but destroys the dice (WaitingState reuses dice at the practice
   * slots to avoid a replace-flash).
   */
  resetToReady(): void {
    this.collectedDice.clear();
    this.accumulatedEnergy = 0;
    this.isHolding = false;
    this.stateMachine.forceTransitionTo(DiceThrowState.READY);
  }

  // ── Pickup Labels ──

  /** Load the pickup texture (shared across all controllers) */
  private ensurePickupTexture(): void {
    if (DiceController.pickupTexture || DiceController.pickupTextureLoading) return;
    DiceController.pickupTextureLoading = true;
    new THREE.TextureLoader().load('/assets/icons/pickup-dice.png', (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      DiceController.pickupTexture = tex;
      DiceController.pickupTextureLoading = false;
      // Texture arrival is an edge like any other: if the last visibility sync
      // wanted labels up but bailed on the missing texture (fresh session, the
      // PNG racing the scene's asset burst), nothing else re-fires until the
      // next state/authorize edge — so close the loop here.
      this.syncPickupLabelVisibility();
    });
  }

  /** Create a pickup label sprite */
  private createPickupLabel(): THREE.Sprite {
    const mat = new THREE.SpriteMaterial({
      map: DiceController.pickupTexture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(mat);
    // Size: roughly 1.2 wide, aspect from the image (~370x310 → ~1.2 aspect)
    const aspect = 370 / 310;
    const height = 1.0;
    sprite.scale.set(height * aspect, height, 1);
    sprite.visible = false;
    return sprite;
  }

  /** Show pickup labels above all uncollected dice */
  showPickupLabels(): void {
    if (!DiceController.pickupTexture) {
      this.ensurePickupTexture();
      return; // Will show on next call once texture loads
    }
    // Ensure we have one label per die
    while (this.pickupLabels.length < this.dice.length) {
      const label = this.createPickupLabel();
      this.engine.scene.add(label);
      this.pickupLabels.push(label);
    }
    // Show labels above uncollected dice, hide for collected
    for (let i = 0; i < this.dice.length; i++) {
      const die = this.dice[i];
      const label = this.pickupLabels[i];
      const isCollected = this.collectedDice.has(die);
      label.visible = !isCollected;
      if (!isCollected) {
        const pos = die.mesh.position;
        label.position.set(pos.x, pos.y + 1.8, pos.z);
      }
    }
    this.pickupLabelsVisible = true;

    // Pop-in animation
    this.pickupLabelPopScale = 0;
    gsap.to(this, { pickupLabelPopScale: 1, duration: 0.35, ease: 'back.out(1.7)' });
  }

  /** Hide all pickup labels */
  hidePickupLabels(): void {
    for (const label of this.pickupLabels) {
      label.visible = false;
    }
    this.pickupLabelsVisible = false;
  }

  /** Remove and dispose all pickup labels */
  private disposePickupLabels(): void {
    for (const label of this.pickupLabels) {
      label.removeFromParent();
      (label.material as THREE.SpriteMaterial).dispose();
    }
    this.pickupLabels = [];
    this.pickupLabelsVisible = false;
  }

  /**
   * Edge-driven pickup-label VISIBILITY. Labels show when: enabled + dice
   * exist + authorized (or non-authoritative) + state allows pickup. Every
   * input is discrete, so this runs on its edges only: the setters
   * (setEnabled / setServerAuthoritativeMode / authorize / revoke), dice
   * create/clear, and throw-state transitions (via onStateChange). The
   * per-frame updatePickupLabels below does POSITIONING only.
   */
  private syncPickupLabelVisibility(): void {
    // The StateMachine constructor fires onStateChange for the INITIAL state
    // before `this.stateMachine` is assigned — nothing to sync yet (no dice,
    // no labels), bail until construction completes.
    if (!this.stateMachine) return;
    const state = this.stateMachine.current;
    const canPickup = this.enabled
      && this.dice.length > 0
      && (!this.isServerAuthoritativeMode || this.isAuthorized)
      && (state === DiceThrowState.READY
        || state === DiceThrowState.STOPPED
        || state === DiceThrowState.PICKING_UP);
    if (canPickup && !this.pickupLabelsVisible) {
      this.showPickupLabels();
    } else if (!canPickup && this.pickupLabelsVisible) {
      this.hidePickupLabels();
    }
  }

  /**
   * Per-frame pickup-label POSITIONING: labels track their (possibly still
   * settling) dice and keep constant screen size against camera distance —
   * the only genuinely per-frame part. Visibility is owned by
   * syncPickupLabelVisibility (edge-driven).
   */
  private updatePickupLabels(): void {
    if (!this.pickupLabelsVisible) return;
    {
      // Update positions, per-die visibility, and constant screen-size scaling
      const camera = this.engine.camera;
      const vFov = camera.fov * (Math.PI / 180);
      // Target height in screen fraction (~0.06 = 6% of screen height)
      const TARGET_SCREEN_HEIGHT = 0.075;
      const baseHeight = 2 * Math.tan(vFov / 2) * TARGET_SCREEN_HEIGHT;
      const aspect = 370 / 310;

      for (let i = 0; i < this.dice.length && i < this.pickupLabels.length; i++) {
        const die = this.dice[i];
        const label = this.pickupLabels[i];
        if (this.collectedDice.has(die)) {
          label.visible = false;
        } else {
          label.visible = true;
          const pos = die.mesh.position;
          label.position.set(pos.x, pos.y + 1.8, pos.z);
          // Scale to maintain constant screen size, with pop-in animation
          const dist = camera.position.distanceTo(label.position);
          const h = baseHeight * dist * this.pickupLabelPopScale;
          label.scale.set(h * aspect, h, 1);
        }
      }
    }
  }

  /**
   * Random face-aligned cube orientation (Rubik's-cube-style snap).
   * Picks uniformly from the 24 cube-aligned orientations: 6 face
   * choices × 4 yaw turns. Keeps the cube oriented to world axes so
   * the throw contacts floor and walls on flat faces, not on edges
   * or corners — feels organic.
   *
   * Rejected alternative (Shoemake's uniform random quaternion): gave
   * arbitrarily tilted starting poses, so dice hit walls at weird
   * angles and bounced off-axis. Looked fine in still frames; felt
   * unnatural in motion.
   */
  private randomFaceAlignedQuaternion(): { x: number; y: number; z: number; w: number } {
    const faceIdx = Math.floor(Math.random() * 6);
    const yawTurns = Math.floor(Math.random() * 4);

    const face = new THREE.Quaternion();
    switch (faceIdx) {
      case 0: /* +Y up: identity */ break;
      case 1: face.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI); break;       // -Y up
      case 2: face.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2); break;   // +Z up
      case 3: face.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2); break;  // -Z up
      case 4: face.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2); break;  // +X up
      case 5: face.setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI / 2); break;   // -X up
    }

    const yaw = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      yawTurns * Math.PI / 2,
    );

    // yaw applied first (around local +Y), then face rotation tips
    // the cube. Result: yaw rotates around whichever face ends up on
    // top, giving 24 distinct cube-aligned orientations.
    const result = new THREE.Quaternion().multiplyQuaternions(face, yaw);
    return { x: result.x, y: result.y, z: result.z, w: result.w };
  }

  /** Release dice assets back to the pool, dispose procedural dice */
  private releaseDiceAssets(): void {
    for (const die of this.dice) {
      if (die.asset && this.assetManager) {
        // Remove from scene + physics, then return mesh to pool
        die.dispose();
        this.assetManager.releaseDice(die.asset);
      } else {
        die.dispose();
      }
    }
  }

  /**
   * Update (call every frame)
   */
  update(deltaTime: number): void {
    this.stateMachine.update(deltaTime);
    this.updatePickupLabels();

    // Held dice tumble continuously and follow the finger while picked up. The
    // finger holding STILL fires no input events, so the input pulse expires and
    // the shooter's own hold drops to the idle heartbeat (8fps) even though the
    // dice are visibly animating. Pulse while anything is collected so the
    // shooter's drag/hold stays 60fps — mirrors the opponent-drag pulse that
    // already keeps spectators smooth.
    if (this.collectedDice.size > 0) {
      renderScheduler.pulse();
      // Held dice are shadow casters moving outside physics — follow them.
      renderScheduler.markShadowsDirty();
    }

    // Update shader uniforms for animated dice skins (holographic, neon glow,
    // etc.) — gated: rooms with only static skins skip the whole block. A
    // time-driven shader is a continuous visible animation, so it also keeps
    // the renderer at full rate (not gsap / physics / input, needs the pulse).
    if (this.assetManager?.hasActiveShaders()) {
      this.assetManager.updateShaderTime(performance.now() / 1000);
      renderScheduler.pulse();
    }

    // (The per-frame lob-force block that lived here was permanently disabled
    // — onThrowingEnter applies a one-shot impulse and force-sets
    // lobActive=false — and has been removed. Restore from git history if the
    // floaty "wind gust" arc experiment ever comes back.)

    // Sync all dice visuals to physics (skip held dice - they're controlled directly)
    for (const die of this.dice) {
      if (!die.held) {
        die.syncToPhysics(deltaTime);
      }
    }
  }

  // ============================================================================
  // INPUT HANDLERS
  // ============================================================================

  private onInputStart(x: number, y: number): void {
    if (!this.enabled) return;

    // In server-authoritative mode, check authorization
    if (this.isServerAuthoritativeMode && !this.isAuthorized) {
      return;
    }

    this.lastInputPosition = { x, y };
    this.resetGestureTrail(x, y);

    // Always track touch state (even during rolling/settling)
    this.isTouching = true;
    this.touchPosition = { x, y };

    const state = this.stateMachine.current;

    // In STOPPED or READY state, player can pick up dice from where they are
    if (state === DiceThrowState.STOPPED || state === DiceThrowState.READY) {
      // If stopped, just transition to ready (no dice teleport!)
      if (state === DiceThrowState.STOPPED) {
        this.collectedDice.clear();
        this.accumulatedEnergy = 0;
        this.holdTime = 0;
        this.stateMachine.forceTransitionTo(DiceThrowState.READY);
      }

      // Emit pickup start event for server (in authoritative mode)
      if (this.isServerAuthoritativeMode) {
        this.emit('dice:pickup_start_request', { timestamp: Date.now() });
        this.serverDicePhase = DicePhase.COLLECTING;
      }

      // Start holding/tracking touch - allows drag-to-collect
      this.isHolding = true;
      this.screenToWorld(x, y, this.config.holdHeight, this.holdPosition);
      this.stateMachine.transitionTo(DiceThrowState.PICKING_UP);

      // Try to pick up a die if touching near one
      const groundPos = this.screenToWorld(x, y, 0.5, _groundScratch);
      const die = this.findDiceNearPoint(groundPos);

      if (die) {
        this.pickupDie(die);
      }
    }
  }

  private onInputMove(x: number, y: number): void {
    if (!this.enabled) return;
    const dx = x - this.lastInputPosition.x;
    const dy = y - this.lastInputPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Always track touch position (even during rolling/settling)
    this.touchPosition = { x, y };

    const state = this.stateMachine.current;

    // Update hold position to follow finger
    if (this.isHolding) {
      this.screenToWorld(x, y, this.config.holdHeight, this.holdPosition);
    }

    if (state === DiceThrowState.PICKING_UP) {
      // Try to collect more dice as we drag over them
      const groundPos = this.screenToWorld(x, y, 0.5, _groundScratch);
      const die = this.findDiceNearPoint(groundPos);
      if (die && !this.collectedDice.has(die)) {
        this.pickupDie(die);
      }

      // Check if all dice collected
      if (this.allDiceCollected()) {
        this.stateMachine.transitionTo(DiceThrowState.HOLDING);
      }
    } else if (state === DiceThrowState.HOLDING) {
      const now = performance.now();

      // Continue accumulating energy (ignore micro jitter with higher deadzone while holding)
      if (distance >= HOLD_DEADZONE_PX) {
        // Mobile boost — see DiceControllerConfig.touchEnergyMultiplier
        // for rationale. No-op on desktop / touch-laptops.
        const gain = isMobilePlatform()
          ? this.config.energyGain * this.config.touchEnergyMultiplier
          : this.config.energyGain;
        this.accumulatedEnergy += distance * gain;
        this.accumulatedEnergy = Math.min(this.accumulatedEnergy, this.config.maxEnergy);

        // Update swipe direction
        const ctx = this.stateMachine.getContext();
        ctx.swipeDirection = {
          x: dx / distance,
          y: dy / distance,
        };

        this.lastEnergyUpdateTime = now;
      } else {
        // Instantly clear energy when essentially stationary while holding
        if (this.accumulatedEnergy !== 0) {
          this.accumulatedEnergy = 0;
          this.lastEnergyUpdateTime = now;
        }
      }

      this.emit('dice:energy_change', {
        energy: this.accumulatedEnergy,
        maxEnergy: this.config.maxEnergy,
      });

      this.detectShake(dx, now);
      this.addGesturePoint(x, y);
    }

    this.lastInputPosition = { x, y };
  }

  /**
   * Shake-rattle: track the sign of horizontal movement; when the direction
   * reverses after a stroke of at least SHAKE_STROKE_MIN_PX, play one rattle
   * beat (cooldown-limited) and emit dice:shake for the network relay. A
   * single-direction throw windup never reverses, so it can't false-positive.
   */
  private detectShake(dx: number, now: number): void {
    const dir = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    if (dir === 0) return;
    if (dir === this.shakeDir) {
      this.shakeStrokePx += Math.abs(dx);
      return;
    }
    const prevStroke = this.shakeStrokePx;
    const strokeDurationMs = Math.max(1, now - this.shakeStrokeStartMs);
    this.shakeDir = dir;
    this.shakeStrokePx = Math.abs(dx);
    this.shakeStrokeStartMs = now;
    const mobile = isMobilePlatform();
    if (prevStroke < (mobile ? SHAKE_STROKE_MIN_PX_MOBILE : SHAKE_STROKE_MIN_PX)) return;
    if (now - this.lastShakeSoundTime < (mobile ? SHAKE_SOUND_COOLDOWN_MS_MOBILE : SHAKE_SOUND_COOLDOWN_MS)) return;
    this.lastShakeSoundTime = now;
    // Beat intensity from the completed stroke's speed: a short fast flick
    // is a hard shake, a long slow drag is a soft one.
    const speed = prevStroke / strokeDurationMs; // px/ms
    const intensity = Math.min(1, Math.max(0, (speed - SHAKE_SPEED_SOFT) / (SHAKE_SPEED_HARD - SHAKE_SPEED_SOFT)));
    sound.play(randomShakeSound(), { volume: SHAKE_VOLUME_MAX * (SHAKE_VOLUME_MIN + (1 - SHAKE_VOLUME_MIN) * intensity) });
    this.emit('dice:shake', { intensity });
  }

  private onInputEnd(_x: number, _y: number): void {
    if (!this.enabled) return;
    // Clear touch tracking
    this.isTouching = false;

    const state = this.stateMachine.current;

    if (state === DiceThrowState.HOLDING) {
      this.isHolding = false;
      const now = performance.now();
      const recentMs = now - this.lastEnergyUpdateTime;
      if (recentMs > 120 && this.accumulatedEnergy > 0) {
        this.accumulatedEnergy = 0;
        this.emit('dice:energy_change', {
          energy: this.accumulatedEnergy,
          maxEnergy: this.config.maxEnergy,
        });
      }

      const hasRecentEnergy = recentMs <= 200;
      const hasEnoughPath = this.gesturePathLength >= MIN_THROW_PATH;

      if (hasRecentEnergy && hasEnoughPath && this.accumulatedEnergy >= this.config.minEnergyToThrow) {
        this.stateMachine.transitionTo(DiceThrowState.THROWING);
      } else {
        // Not enough energy — drop the dice back to the table. Broadcast the
        // drop transforms FIRST so spectators replay the same fall (otherwise
        // their copy of our dice freezes mid-air "as if grabbed").
        this.emitDropRequest();
        this.releaseAllDice();
        this.stateMachine.transitionTo(DiceThrowState.READY);
      }
    } else if (state === DiceThrowState.PICKING_UP) {
      // Cancelled pickup — same as a HOLDING drop, relay the fall to spectators.
      this.isHolding = false;
      this.emitDropRequest();
      this.releaseAllDice();
      this.stateMachine.transitionTo(DiceThrowState.READY);
    }
  }

  // ============================================================================
  // STATE HANDLERS
  // ============================================================================

  private onReadyEnter(): void {
    this.accumulatedEnergy = 0;
    this.collectedDice.clear();
    this.isHolding = false;
    this.holdTime = 0;
    this.resetGestureTrail();

  }

  private onPickingUpEnter(): void {
    // Start drag sync interval for network
    this.startDragSync();

    // Reset energy/swing at pickup start so power only comes from deliberate post-collect swipe
    this.accumulatedEnergy = 0;
    this.lastEnergyUpdateTime = performance.now();
    const stoppedCtx = this.stateMachine.getContext();
    stoppedCtx.swipeDirection = { x: 0, y: 0 };
    this.emit('dice:energy_change', {
      energy: this.accumulatedEnergy,
      maxEnergy: this.config.maxEnergy,
    });

    this.resetGestureTrail();
  }

  private onPickingUpUpdate(deltaTime: number): void {
    this.updateHeldDicePositions(deltaTime);
  }

  private onHoldingEnter(): void {
    this.emit('dice:all_collected', undefined);

    // Reset energy so throw power only comes from deliberate post-collect swipe (prevents pickup path from forcing hard throws)
    this.accumulatedEnergy = 0;
    this.lastEnergyUpdateTime = performance.now();
    // Fresh shake state per hold — a leftover stroke from the previous hold
    // must not count toward this hold's first reversal.
    this.shakeDir = 0;
    this.shakeStrokePx = 0;
    this.shakeStrokeStartMs = performance.now();
    const stoppedCtx = this.stateMachine.getContext();
    stoppedCtx.swipeDirection = { x: 0, y: 0 };
    this.emit('dice:energy_change', {
      energy: this.accumulatedEnergy,
      maxEnergy: this.config.maxEnergy,
    });

    // Emit collected event for server (in authoritative mode)
    if (this.isServerAuthoritativeMode) {
      this.emit('dice:collected_request', { timestamp: Date.now() });
      this.serverDicePhase = DicePhase.HOLDING;
    }

    this.resetGestureTrail();
  }

  private onHoldingUpdate(deltaTime: number): void {
    this.holdTime += deltaTime;
    this.updateHeldDicePositions(deltaTime);
  }

  private onThrowingEnter(): void {
    // Stop drag sync - we're about to throw
    this.stopDragSync();

    const ctx = this.stateMachine.getContext();
    const power = (this.accumulatedEnergy / this.config.maxEnergy) * this.config.maxPower;

    // Calculate throw direction (forward only - lob is applied as force over time)
    const forwardDir = {
      x: ctx.swipeDirection.x * 0.3,
      z: -1 * this.config.baseForward,
    };

    // Normalize forward direction and scale by power
    const forwardLength = Math.sqrt(forwardDir.x ** 2 + forwardDir.z ** 2) || 1;
    const direction = {
      x: (forwardDir.x / forwardLength) * power,
      y: 0.1 * power,  // Tiny upward so dice don't scrape the ground
      z: (forwardDir.z / forwardLength) * power,
    };

    // Apply lob as a single upward impulse at throw time (deterministic).
    // Previously this was a per-frame force over lobDuration which caused
    // non-deterministic results due to variable frame timing.
    const lobImpulse = this.config.lobStrength * 5; // Tuned to match previous arc height
    for (const die of this.dice) {
      if (!die.held) {
        const impulseY = lobImpulse * die.body.mass;
        die.body.applyImpulse(
          new CANNON.Vec3(0, impulseY, 0),
          die.body.position
        );
      }
    }
    // (Per-frame lob permanently disabled — one-shot impulse above is the
    // whole throw arc; dead lob fields removed.)

    // Throw spin: power-scaled magnitude, swipe-direction bias on Y
    // (yaw — feels like wrist motion), per-die random for X / Z so the
    // 3 dice tumble independently. Min baseline guarantees a visible
    // tumble even on a soft throw, so dice never go dead-straight.
    //
    // Determinism is preserved: each die's angularVelocity is captured
    // into diceStates below and broadcast via throw_request — other
    // clients apply the exact same value.
    const power01 = Math.min(1, Math.max(0, this.accumulatedEnergy / this.config.maxEnergy));
    const SPIN_MIN = 5;
    const SPIN_MAX = 16;
    const spinMag = SPIN_MIN + (SPIN_MAX - SPIN_MIN) * power01;
    const swipeYawBias = ctx.swipeDirection.x; // -1..1, left/right wrist

    // Throw all dice and capture their states immediately
    const diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }> = [];

    for (let i = 0; i < this.dice.length; i++) {
      const die = this.dice[i];

      // Snap to a random face-aligned orientation right before the
      // throw (Rubik's-cube-style: pick one of 24 cube orientations).
      // Without this, dice carry the pickup-time pose (= last stopped
      // pose) all the way through the hand → release, clustering
      // outcomes around whatever face was on top when grabbed. The
      // chosen quaternion is captured into getRotation() below and
      // broadcast via throw_request — determinism preserved across
      // clients.
      //
      // Why face-aligned, not arbitrary 3D random: a uniform-random
      // quaternion (Shoemake) gives weird tilted poses, so the cube
      // contacts walls / floor on edges or corners and bounces in
      // non-organic directions. Face-aligned starts keep the throw
      // physics natural while still randomizing the initial face.
      //
      // No angular velocity is applied — dice fly straight from the
      // hand, the random face × floor/wall contacts produce the final
      // outcome distribution.
      const randomQuat = this.randomFaceAlignedQuaternion();
      die.setRotationFromQuaternion(randomQuat);

      // Per-die spin axis: random X / Z components (independent tumble),
      // Y biased by swipe direction (shared wrist gesture). All clients
      // recompute the SAME random values? No — local computes, broadcast
      // via diceStates below.
      const spin = this.makeThrowSpin(spinMag, swipeYawBias);

      die.throw(
        direction,
        1,
        spin,
        this.config.diceMass,
      );

      // Capture state immediately after throw
      diceStates.push({
        position: die.getPosition(),
        rotation: die.getRotation(),
        velocity: die.getVelocity(),
        angularVelocity: die.getAngularVelocity(),
      });
    }

    // Stuck-throw prevention (Phone Dice's preRun): simulate the captured
    // throw ahead of time; if it wedges/leans/never settles, re-randomize
    // the orientation + tumble (the gesture's velocity is untouched) and
    // re-evaluate. Mutates diceStates in place to the ACCEPTED candidate,
    // which is what gets broadcast below — spectators replay the same
    // validated throw. Skipped for HI_LO (deferred physics + multi-player
    // dice interactions make a solo pre-run meaningless there).
    if (!this.deferPhysicsOnThrow) {
      this.preRunAndFixThrow(diceStates, spinMag, swipeYawBias);
    }

    // Update server phase tracking
    if (this.isServerAuthoritativeMode) {
      this.serverDicePhase = DicePhase.THROWN;
      this.emit('dice:throw_request', {
        diceStates,
        halfExtents: this.dice[0]?.physicsHalfExtents,
        halfExtentsPerDie: this.captureHalfExtentsPerDie(),
        timestamp: Date.now(),
      });
    }

    // HI_LO determinism: freeze dice after capturing state.
    // Physics will only start when applyServerThrow() arrives from server echo.
    // This ensures all dice (local + opponent) enter physics from server broadcasts.
    if (this.deferPhysicsOnThrow) {
      for (const die of this.dice) {
        die.pickup(); // mass=0, no collision — freezes die at throw position
      }
    }

    this.resetGestureTrail();

    // Emit throw sync event with full physics state for network (legacy)
    this.emit('dice:throw_sync', { diceStates });

    sound.play(SoundId.DICE_THROW);
    this.emit('dice:throw', {
      direction: { x: ctx.swipeDirection.x, y: ctx.swipeDirection.y },
      power,
    });

    // Transition to rolling (unless deferred — applyServerThrow will force ROLLING)
    if (!this.deferPhysicsOnThrow) {
      this.stateMachine.transitionTo(DiceThrowState.ROLLING);
    }
  }

  /**
   * Per-die throw spin: random X / Z tumble axes, Y biased by the swipe
   * (wrist yaw), normalized and scaled to spinMag. Shared by the initial
   * throw and pre-run regeneration so re-rolled candidates draw from the
   * same distribution as first throws.
   */
  private makeThrowSpin(spinMag: number, swipeYawBias: number): { x: number; y: number; z: number } {
    const axRandX = (Math.random() - 0.5) * 2;        // -1..1
    const axRandZ = (Math.random() - 0.5) * 2;
    const axY = swipeYawBias * 1.5 + (Math.random() - 0.5) * 0.5;
    const axLen = Math.sqrt(axRandX * axRandX + axY * axY + axRandZ * axRandZ) || 1;
    return {
      x: (axRandX / axLen) * spinMag,
      y: (axY / axLen) * spinMag,
      z: (axRandZ / axLen) * spinMag,
    };
  }

  /**
   * Evaluate the throw loaded into the dice bodies via preRunThrow; while it
   * fails (wedged / leaning / still moving at the frame budget), re-randomize
   * each die's orientation + tumble and re-evaluate. Velocity — the part the
   * player authored with their gesture — is never touched, so the throw feel
   * is identical; only the die randomness changes, which a first throw drew
   * from the same distributions anyway.
   *
   * Runs synchronously between fixed updates: the bodies still hold the
   * captured initial state, no physics step has consumed it yet, so editing
   * quaternion/angularVelocity here is invisible. `diceStates` is updated in
   * place to match the accepted candidate (it's broadcast right after).
   *
   * Gives up after MAX_ATTEMPTS evaluations or TIME_BUDGET_MS wall-clock
   * (slow devices) and keeps the last candidate — the live nudge/dampen
   * pipeline remains as the rescue path, exactly as before this existed.
   */
  private preRunAndFixThrow(
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>,
    spinMag: number,
    swipeYawBias: number,
  ): void {
    if (this.dice.length === 0) return;
    const world = this.engine.physics.world;
    const dt = this.engine.fixedDelta;
    const t0 = performance.now();

    // The simulated steps fire real 'collide' events on the dice — mute
    // globally for the duration (always restored via finally).
    sound.suspend(true);
    try {
      for (let attempt = 1; attempt <= PRERUN_MAX_ATTEMPTS; attempt++) {
        const verdict = preRunThrow(world, this.dice, PRERUN_FRAME_BUDGET, dt);
        if (verdict.ok) {
          if (attempt > 1) {
          }
          return;
        }
        if (attempt === PRERUN_MAX_ATTEMPTS || performance.now() - t0 > PRERUN_TIME_BUDGET_MS) {
          console.warn(`[ThrowPreRun] giving up after ${attempt} attempt(s) (${verdict.reason}) — live nudge/dampen will rescue`);
          return;
        }

        // Reject — redraw the random components and try again.
        for (let i = 0; i < this.dice.length; i++) {
          const die = this.dice[i];
          die.setRotationFromQuaternion(this.randomFaceAlignedQuaternion());
          const spin = this.makeThrowSpin(spinMag, swipeYawBias);
          die.body.angularVelocity.set(spin.x, spin.y, spin.z);
          diceStates[i].rotation = die.getRotation();
          diceStates[i].angularVelocity = die.getAngularVelocity();
        }
      }
    } finally {
      sound.suspend(false);
      // CRITICAL determinism fix — re-apply the accepted throw vectors to the
      // live dice (mirrors Phone Dice's playerShoot re-applying vectors after
      // preRun). The pre-run STEPS the live dice, which SLEEP mid-sim; cannon
      // zeroes a sleeping body's inertia tensor, and preRunThrow's snapshot
      // restores pose/velocity but NOT that tensor. So after the pre-run the
      // dice body is subtly corrupted, and the shooter's real roll diverges
      // from every spectator (who simulate the clean broadcast vectors) — the
      // dice "land in one spot then snap to another" on the result. Resetting
      // the body from diceStates and calling release() rebuilds mass/inertia,
      // wakes the body, and re-arms the dice-vs-dice flight mute, so the
      // shooter rolls bit-identically to what it broadcast. Verified 0.0
      // shooter-vs-spectator divergence (was ~2.5 units). diceStates holds the
      // accepted candidate whether the first attempt passed or a later one did.
      for (let i = 0; i < this.dice.length && i < diceStates.length; i++) {
        const die = this.dice[i];
        const s = diceStates[i];
        die.body.velocity.setZero();
        die.body.angularVelocity.setZero();
        die.body.force.setZero();
        die.body.torque.setZero();
        die.setPosition(s.position);
        die.setRotationFromQuaternion(s.rotation);
        die.setVelocity(s.velocity);
        die.setAngularVelocity(s.angularVelocity);
        die.release();
      }
    }
  }

  private onRollingEnter(): void {
    const ctx = this.stateMachine.getContext();
    ctx.rollStartTime = performance.now();
    this.emit('dice:rolling', undefined);
  }

  private onRollingUpdate(_deltaTime: number): void {
    // Check if dice are slowing down
    if (this.areDiceSlowing()) {
      this.stateMachine.transitionTo(DiceThrowState.SETTLING);
    }
  }

  // Watchdog: hard cap on how long SETTLING may run before forcing STOPPED.
  // The throw pre-run guarantees accepted throws sleep on a valid face within
  // budget, so this normally never fires. It only catches the rare pre-run
  // give-up (5 failed regen attempts) where a die could perpetually slide —
  // without it, removing the live dampen below could hang the local roll.
  // Shooter-only timing; it does not touch physics, so spectator determinism
  // is unaffected.
  private static readonly SETTLE_WATCHDOG_MS = 6000;

  private onSettlingEnter(): void {
    const ctx = this.stateMachine.getContext();
    ctx.settleStartTime = performance.now();
  }

  private onSettlingUpdate(_deltaTime: number): void {
    // No live dampenSliding / nudgeToNearestFace correction here anymore.
    // Those mutated the dice body ONLY on the shooter — spectators never
    // replayed them — which forked the shooter's trajectory from every
    // spectator's and forced the end-of-roll snap. The throw pre-run already
    // rejects throws that wedge, lean, or never settle and re-randomizes until
    // one settles cleanly on a valid face, so the live roll inherits a clean
    // throw that needs no correction. Shooter and spectators now step
    // bit-identical physics from release to rest. We just wait for sleep (or
    // the watchdog) and read the result wherever the dice land.
    const ctx = this.stateMachine.getContext();
    const settlingTime = performance.now() - ctx.settleStartTime;
    if (this.areDiceStopped() || settlingTime > DiceController.SETTLE_WATCHDOG_MS) {
      this.stateMachine.transitionTo(DiceThrowState.STOPPED);
    }
  }

  private onStoppedEnter(): void {
    // Dice are at rest — none are "collected" anymore (ready to be picked up again)
    this.collectedDice.clear();
    this.accumulatedEnergy = 0;
    this.lastEnergyUpdateTime = 0;
    const stoppedCtx = this.stateMachine.getContext();
    stoppedCtx.swipeDirection = { x: 0, y: 0 };
    this.resetGestureTrail();

    // Read dice values
    const values: number[] = [];
    for (const die of this.dice) {
      values.push(die.readValue());
    }

    stoppedCtx.diceResults = values;

    // Capture final pose per die so spectators can correct any divergence
    const finalStates = this.dice.map((die) => ({
      position: die.getPosition(),
      quaternion: die.getRotation(),
    }));

    // Settled-dice collision model is phase-dependent (parkOnStop):
    // - Single-shooter phases (default): park as non-colliding so our settled
    //   dice can't interfere with the next roller's in-flight dice on THIS
    //   client only (the same dice are mass-0 opponent replicas elsewhere) —
    //   that asymmetry forked the sim and forced the end-of-roll snap. The
    //   pickup→release path restores collision if this die is thrown again.
    // - HI_LO (parkOnStop=false): settled dice stay LIVE sleeping colliders.
    //   Another contender's throw physically knocks them (with gravity) — and
    //   the same body exists on the server (HiLoPhysicsWorld.settleDie) and on
    //   every spectator at the same finalStates pose, so the knock computes
    //   identically everywhere. sleep() zeroes velocities; a watchdog-forced
    //   stop on a still-moving die freezes it in place either way.
    for (const die of this.dice) {
      if (this.parkOnStop) {
        die.parkAtRest();
      } else {
        die.body.sleep();
      }
    }

    // Update server phase tracking and emit stopped request
    if (this.isServerAuthoritativeMode) {
      this.serverDicePhase = DicePhase.STOPPED;
      this.emit('dice:stopped_request', {
        results: values,
        finalStates,
        timestamp: Date.now(),
      });
      // Revoke authorization after completing a roll
      this.isAuthorized = false;
      this.syncPickupLabelVisibility();
    }

    this.emit('dice:stopped', { values });

    // If player is already touching, auto-start pickup (no need to lift and re-press)
    // But only if still authorized (or not in authoritative mode)
    if (this.isTouching && (!this.isServerAuthoritativeMode || this.isAuthorized)) {
      this.collectedDice.clear();
      this.accumulatedEnergy = 0;
      this.holdTime = 0;
      this.lastInputPosition = { ...this.touchPosition };

      // Start holding/tracking
      this.isHolding = true;
      this.screenToWorld(
        this.touchPosition.x,
        this.touchPosition.y,
        this.config.holdHeight,
        this.holdPosition
      );
      this.stateMachine.forceTransitionTo(DiceThrowState.PICKING_UP);

      // Try to pick up a die if touching near one
      const groundPos = this.screenToWorld(this.touchPosition.x, this.touchPosition.y, 0.5, _groundScratch);
      const die = this.findDiceNearPoint(groundPos);
      if (die) {
        this.pickupDie(die);
      }
    }

    this.resetGestureTrail();
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  private resetGestureTrail(initialX?: number, initialY?: number): void {
    this.gesturePoints = [];
    this.gesturePathLength = 0;
    this.gestureArcAngle = 0;
    if (typeof initialX === 'number' && typeof initialY === 'number') {
      this.gesturePoints.push({ x: initialX, y: initialY });
    }
  }

  private addGesturePoint(x: number, y: number): void {
    const MIN_SEG_LEN = 2;
    if (this.gesturePoints.length === 0) {
      this.gesturePoints.push({ x, y });
      return;
    }

    const last = this.gesturePoints[this.gesturePoints.length - 1];
    const vx = x - last.x;
    const vy = y - last.y;
    const segLen = Math.sqrt(vx * vx + vy * vy);
    if (segLen < MIN_SEG_LEN) return;

    if (this.gesturePoints.length >= 2) {
      const prev = this.gesturePoints[this.gesturePoints.length - 2];
      const pvx = last.x - prev.x;
      const pvy = last.y - prev.y;
      const prevLen = Math.sqrt(pvx * pvx + pvy * pvy);
      if (prevLen >= MIN_SEG_LEN) {
        const cross = pvx * vy - pvy * vx;
        const dot = pvx * vx + pvy * vy;
        const angle = Math.atan2(cross, dot);
        this.gestureArcAngle += angle;
      }
    }

    this.gesturePathLength += segLen;
    this.gesturePoints.push({ x, y });
  }

  /**
   * Convert screen coordinates to world position at a given height.
   * Writes into `target` (no per-call allocation — this runs on every
   * pointer-move during pickup/drag) and returns it. Falls back to
   * (0, height, 0) when the ray misses the plane (grazing angle).
   */
  private screenToWorld(screenX: number, screenY: number, height: number, target: THREE.Vector3): THREE.Vector3 {
    const size = this.engine.renderer.getSize();
    sharedPointer.x = (screenX / size.width) * 2 - 1;
    sharedPointer.y = -(screenY / size.height) * 2 + 1;

    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    // Use shared plane for intersection
    tempPlane.setFromNormalAndCoplanarPoint(
      tempVec3A.set(0, 1, 0),
      tempVec3B.set(0, height, 0)
    );
    if (!sharedRaycaster.ray.intersectPlane(tempPlane, target)) {
      target.set(0, height, 0);
    }
    return target;
  }

  /**
   * Find a die near a world position (within pickup radius)
   */
  private findDiceNearPoint(worldPos: THREE.Vector3): Dice | null {
    for (const die of this.dice) {
      if (this.collectedDice.has(die)) continue;

      const diePos = die.getPosition();
      const dist = Math.sqrt(
        Math.pow(diePos.x - worldPos.x, 2) +
        Math.pow(diePos.z - worldPos.z, 2)
      );

      if (dist < this.config.pickupRadius) {
        return die;
      }
    }
    return null;
  }

  private pickupDie(die: Dice): void {
    die.pickup();
    this.collectedDice.add(die);
    sound.play(SoundId.DICE_PICKUP);
    this.emit('dice:pickup', { diceIndex: this.dice.indexOf(die) });
  }

  /**
   * Relay a no-throw release (drop) to spectators. The held dice are falling
   * back to the table via local physics; capture each one's current pose with
   * ZERO velocity so every other client replays a bit-identical gravity drop
   * (same deterministic settle as a throw, just no launch). No-op outside
   * server-authoritative mode or when nothing is held. NOT a roll — the server
   * resets the phase to IDLE and never reads a result from this.
   *
   * The snapshot covers ALL of our dice, not just the held ones. Spectators
   * grab every die into their drag group on the first drag packet (they can't
   * know how many we actually hold), so a partial payload strands the extras —
   * index-compressed to the wrong die, frozen at drag-group-local coordinates
   * (the "two dice stuck at the floor" spam bug). A full snapshot keeps
   * spectator die i == local die i and makes every drop a self-healing re-sync:
   * held dice fall from the hand, resting dice re-assert their floor pose.
   */
  private emitDropRequest(): void {
    if (!this.isServerAuthoritativeMode) return;
    if (this.collectedDice.size === 0) return;

    const diceStates = this.dice.map((die) => ({
      position: die.getPosition(),
      rotation: die.getRotation(),
      velocity: { x: 0, y: 0, z: 0 },
      angularVelocity: { x: 0, y: 0, z: 0 },
    }));

    this.serverDicePhase = DicePhase.IDLE;
    this.emit('dice:drop_request', {
      diceStates,
      halfExtents: this.dice[0]?.physicsHalfExtents,
      halfExtentsPerDie: this.captureHalfExtentsPerDie(),
      timestamp: Date.now(),
    });
  }

  /**
   * Snapshot every die's collider half-extents, index-aligned with diceStates.
   * Slots can hold different dice models (or a fallback cube while a slot's
   * asset is still loading), so the dice[0]-only legacy field under-describes
   * the throw — a spectator simulating dice 1/2 with die 0's shape forks the
   * replay and forces the end-of-roll face snap. Cloned because
   * setPhysicsHalfExtents mutates the die's array in place.
   */
  private captureHalfExtentsPerDie(): Array<[number, number, number]> {
    return this.dice.map((die) => [...die.physicsHalfExtents] as [number, number, number]);
  }

  private releaseAllDice(): void {
    this.stopDragSync();
    for (const die of this.collectedDice) {
      die.release();
    }
    this.collectedDice.clear();
  }

  private startDragSync(): void {
    if (this.dragSyncInterval) return; // Already running

    this.dragSyncInterval = setInterval(() => {
      this.emitDragSync();
    }, this.DRAG_SYNC_INTERVAL);

    // Also emit immediately
    this.emitDragSync();
  }

  private stopDragSync(): void {
    if (this.dragSyncInterval) {
      clearInterval(this.dragSyncInterval);
      this.dragSyncInterval = null;
    }
  }

  private emitDragSync(): void {
    if (this.collectedDice.size === 0) return;

    // Get average position of collected dice (or first die position)
    const firstDie = this.collectedDice.values().next().value;
    if (!firstDie) return;

    const position = this.isHolding
      ? { x: this.holdPosition.x, y: this.holdPosition.y, z: this.holdPosition.z }
      : firstDie.getPosition();
    const rotation = firstDie.getRotation();
    const timestamp = Date.now();
    this.dragSeq += 1;

    this.emit('dice:drag_sync', {
      position,
      rotation,
      seq: this.dragSeq,
      timestamp,
    });
  }

  private updateHeldDicePositions(deltaTime: number): void {
    if (!this.isHolding) return;

    const spacing = 1.2;
    const count = this.collectedDice.size;
    let index = 0;

    for (const die of this.collectedDice) {
      // Calculate target position - offset each die slightly from hold position
      const offsetX = (index - (count - 1) / 2) * spacing;
      const targetPos = _heldTarget.set(
        this.holdPosition.x + offsetX,
        this.config.holdHeight,
        this.holdPosition.z
      );

      // Smooth lerp to follow finger (magnetic attachment)
      const currentPos = die.getPosition();
      const lerpedPos = _heldLerped.set(
        currentPos.x + (targetPos.x - currentPos.x) * this.MAGNET_SPEED * deltaTime,
        currentPos.y + (targetPos.y - currentPos.y) * this.MAGNET_SPEED * deltaTime,
        currentPos.z + (targetPos.z - currentPos.z) * this.MAGNET_SPEED * deltaTime
      );

      die.setPosition({
        x: lerpedPos.x,
        y: lerpedPos.y,
        z: lerpedPos.z,
      });

      // Continuous rotation while held (like prototype)
      // Each die rotates at slightly different rates for variety
      die.addRotation({
        x: deltaTime * 2 * (1 + index * 0.1),
        y: 0,
        z: deltaTime * 1.5 * (1 + index * 0.1),
      });

      index++;
    }
  }

  private allDiceCollected(): boolean {
    return this.collectedDice.size === this.dice.length;
  }

  private areDiceSlowing(): boolean {
    const threshold = 2; // Speed threshold
    for (const die of this.dice) {
      if (die.getSpeed() > threshold) {
        return false;
      }
    }
    return true;
  }

  private areDiceStopped(): boolean {
    for (const die of this.dice) {
      if (!die.isStopped()) {
        return false;
      }
    }
    return true;
  }

  /**
   * Get current state
   */
  get state(): DiceThrowState | null {
    return this.stateMachine.current;
  }

  /**
   * Get current energy
   */
  get energy(): number {
    return this.accumulatedEnergy;
  }

  /**
   * Get dice count collected
   */
  get collected(): number {
    return this.collectedDice.size;
  }

  /**
   * Get dice results (after stopped)
   */
  get results(): number[] {
    return this.stateMachine.getContext().diceResults;
  }

  /**
   * Enable or disable dice input
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.syncPickupLabelVisibility();
  }

  /**
   * Check if dice input is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get dice array (for camera tracking)
   */
  getDice(): Dice[] {
    return this.dice;
  }

  /**
   * Reset to ready state
   */
  reset(positions: Array<{ x: number; y: number; z: number }>): void {
    this.collectedDice.clear();
    this.accumulatedEnergy = 0;
    this.holdTime = 0;

    for (let i = 0; i < this.dice.length; i++) {
      this.dice[i].reset(positions[i] ?? { x: 0, y: 1, z: 0 });
    }

    this.stateMachine.forceTransitionTo(DiceThrowState.READY);
  }

  // ============================================================================
  // SERVER-AUTHORITATIVE METHODS
  // ============================================================================

  /**
   * Enable server-authoritative mode
   * When enabled, local actions are gated by server authorization
   */
  getServerAuthoritativeMode(): boolean {
    return this.isServerAuthoritativeMode;
  }

  setServerAuthoritativeMode(enabled: boolean): void {
    this.isServerAuthoritativeMode = enabled;
    if (enabled) {
      this.isAuthorized = false;
    }
    this.syncPickupLabelVisibility();
  }

  /**
   * Defer physics on throw (for HI_LO determinism).
   * When true, onThrowingEnter captures throw state and sends to server
   * but freezes dice until applyServerThrow() arrives.
   * This ensures all clients start physics from the same server broadcast.
   */
  setDeferPhysicsOnThrow(defer: boolean): void {
    this.deferPhysicsOnThrow = defer;
  }

  /** Whether local physics is deferred to the server echo (HI_LO mode). */
  isPhysicsDeferred(): boolean {
    return this.deferPhysicsOnThrow;
  }

  /**
   * Whether onStoppedEnter parks settled dice as non-colliders (single-shooter
   * clean-world model) or leaves them as live sleeping colliders (HI_LO —
   * settled dice are knockable and fall with gravity, matched by the server
   * world and every spectator). Set per roll authorization in setupLocalRoll.
   */
  setParkOnStop(park: boolean): void {
    this.parkOnStop = park;
  }

  /**
   * Pickup all dice immediately (disable physics).
   * Used during HI_LO to prevent dice from having active physics
   * between creation and player interaction.
   */
  pickupAllDice(): void {
    for (const die of this.dice) {
      die.pickup();
    }
  }

  /**
   * Server authorized us to roll
   */
  handleRollAuthorize(): void {
    this.isAuthorized = true;
    this.serverDicePhase = DicePhase.IDLE;
    this.syncPickupLabelVisibility();
  }

  /**
   * Handle dice phase change from server
   * This is called for remote players' dice phases
   */
  handleDicePhase(playerId: string, phase: DicePhase, throwParams?: DiceThrowParams): void {

    // This is for remote players - emit event for GhostDiceManager to handle
    this.emit('dice:remote_phase', { playerId, phase, throwParams });

    // If it's a throw with params, we need to apply the physics on ghost dice
    if (phase === DicePhase.THROWN && throwParams) {
      this.emit('dice:remote_throw', { playerId, throwParams });
    }
  }

  /**
   * Apply throw physics from server (for our own throw, echoed back)
   * This ensures deterministic simulation across all clients by applying
   * the exact same initial physics state on all clients.
   */
  applyServerThrow(throwParams: DiceThrowParams): void {
    for (let i = 0; i < throwParams.diceStates.length && i < this.dice.length; i++) {
      const state = throwParams.diceStates[i];
      const die = this.dice[i];

      // First, fully reset the body to clear any accumulated state
      // This is critical for deterministic simulation
      die.body.velocity.setZero();
      die.body.angularVelocity.setZero();
      die.body.force.setZero();
      die.body.torque.setZero();

      // Route through setPosition / setRotationFromQuaternion so the
      // PhysicsWorld interpolation state is refreshed alongside body + mesh —
      // same as the opponent replay path (OpponentDiceManager.handleThrow).
      // Direct body/mesh writes leave previousPosition stale, so the first
      // render frame after release lerps from the pre-throw pose.
      die.setPosition(state.position);
      die.setRotationFromQuaternion(state.rotation);
      die.setVelocity(state.velocity);
      die.setAngularVelocity(state.angularVelocity);

      // Now release - enables physics (mass=1, wakes body)
      die.release();
    }

    // Clear held state
    this.collectedDice.clear();
    this.isHolding = false;

    // Transition to rolling state
    if (this.stateMachine.current !== DiceThrowState.ROLLING) {
      this.stateMachine.forceTransitionTo(DiceThrowState.ROLLING);
    }
  }

  /**
   * Get server-authoritative phase
   */
  getServerPhase(): DicePhase {
    return this.serverDicePhase;
  }

  /**
   * Check if authorized to roll
   */
  isRollAuthorized(): boolean {
    return this.isAuthorized;
  }

  /**
   * Revoke roll authorization
   */
  revokeAuthorization(): void {
    this.isAuthorized = false;
    this.syncPickupLabelVisibility();
  }

  // ============================================================================
  // CONFIG METHODS (for editor live tweaking)
  // ============================================================================

  /**
   * Update config at runtime (for editor live tweaking)
   */
  updateConfig(updates: Partial<DiceControllerConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current config
   */
  getConfig(): Required<DiceControllerConfig> {
    return { ...this.config };
  }

  /**
   * Dispose
   */
  dispose(): void {
    this.stopDragSync();
    for (const die of this.dice) {
      die.dispose();
    }
    this.dice = [];
    this.removeAllListeners();
  }
}
